import React, {useEffect, useState} from "react";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {Divider} from "primereact/divider";
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {Tag} from "primereact/tag";
import {useBillingSetting} from "../../../models/billing-setting/use-billing-setting";
import {useTranslate} from "../../../system/useTranslate";
import {
    DataState,
    FormDialog,
    FormField,
    SalesSectionHeader,
    StatCard,
    SwitchField,
} from "../components/common";
import {getErrorMessage} from "../sales-utils";

const emptySettings = {
    defaultCurrency: "IRR",
    invoicePrefix: "INV",
    defaultTaxRate: 0,
    paymentExpirationMinutes: 30,
    zarinpalEnabled: false,
    zarinpalSandbox: true,
    zarinpalCallbackUrl: "",
    paymentResultUrl: "",
    bankAccounts: [],
    manualPaymentInstructions: "",
    supportChatUrl: "",
    supportMessengerUrl: "",
    salesPhone: "",
};

const emptyBankAccount = {
    bankName: "",
    accountHolder: "",
    cardNumber: "",
    iban: "",
    accountNumber: "",
    active: true,
};

function asFormText(value) {
    return value === null || value === undefined ? "" : String(value);
}

function asFormNumber(value, fallback) {
    if (value === null || value === undefined || value === "") return fallback;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
}

function normalizedSettings(setting) {
    if (!setting) return {...emptySettings};
    return {
        defaultCurrency: asFormText(setting.defaultCurrency) || emptySettings.defaultCurrency,
        invoicePrefix: asFormText(setting.invoicePrefix) || emptySettings.invoicePrefix,
        defaultTaxRate: asFormNumber(setting.defaultTaxRate, emptySettings.defaultTaxRate),
        paymentExpirationMinutes: asFormNumber(
            setting.paymentExpirationMinutes,
            emptySettings.paymentExpirationMinutes
        ),
        zarinpalEnabled: setting.zarinpalEnabled === true,
        zarinpalSandbox: setting.zarinpalSandbox !== false,
        zarinpalCallbackUrl: asFormText(setting.zarinpalCallbackUrl),
        paymentResultUrl: asFormText(setting.paymentResultUrl),
        bankAccounts: Array.isArray(setting.bankAccounts)
            ? setting.bankAccounts.map((account) => ({
                ...(account.id ? {id: account.id} : {}),
                bankName: asFormText(account.bankName),
                accountHolder: asFormText(account.accountHolder),
                cardNumber: asFormText(account.cardNumber),
                iban: asFormText(account.iban),
                accountNumber: asFormText(account.accountNumber),
                active: account.active !== false,
            }))
            : [],
        manualPaymentInstructions: asFormText(setting.manualPaymentInstructions),
        supportChatUrl: asFormText(setting.supportChatUrl),
        supportMessengerUrl: asFormText(setting.supportMessengerUrl),
        salesPhone: asFormText(setting.salesPhone),
    };
}

function isHttpUrl(value) {
    try {
        return ["http:", "https:"].includes(new URL(String(value)).protocol);
    } catch {
        return false;
    }
}

function InfoRow({label, value, ltr}) {
    return (
        <div className="flex flex-column md:flex-row md:align-items-center justify-content-between gap-2 py-2 border-bottom-1 border-100">
            <span className="text-xs text-600">{label}</span>
            <span className={`text-xs font-medium text-900 ${ltr ? "ltr text-left line-height-3" : ""}`}>{value || "-"}</span>
        </div>
    );
}

function PaymentSettingsTab() {
    const translate = useTranslate();
    const {
        billingSetting,
        loading,
        error,
        getBillingSettings,
        saveBillingSettings,
    } = useBillingSetting();
    const [visible, setVisible] = useState(false);
    const [form, setForm] = useState(emptySettings);
    const [opening, setOpening] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        getBillingSettings().catch(() => undefined);
    }, [getBillingSettings]);

    const localizedError = (requestError, fallbackKey = "sales-unexpected-error") => {
        const message = getErrorMessage(requestError, fallbackKey);
        const translated = translate(message);
        return translated === message ? translate(fallbackKey) : translated;
    };

    const openDialog = async () => {
        setFormError(null);
        setSuccess(null);
        setOpening(true);
        try {
            const currentSetting = await getBillingSettings();
            setForm(normalizedSettings(currentSetting));
            setVisible(true);
        } catch (requestError) {
            setFormError(localizedError(requestError));
        } finally {
            setOpening(false);
        }
    };

    const updateBankAccount = (index, field, value) => {
        setForm((current) => ({
            ...current,
            bankAccounts: current.bankAccounts.map((account, accountIndex) => (
                accountIndex === index ? {...account, [field]: value} : account
            )),
        }));
    };

    const removeBankAccount = (index) => {
        setForm((current) => ({
            ...current,
            bankAccounts: current.bankAccounts.filter((_, accountIndex) => accountIndex !== index),
        }));
    };

    const addBankAccount = () => {
        setForm((current) => ({
            ...current,
            bankAccounts: [...current.bankAccounts, {...emptyBankAccount}],
        }));
    };

    const save = async (event) => {
        event.preventDefault();
        setFormError(null);

        if (!form.defaultCurrency.trim() || !form.invoicePrefix.trim() || form.paymentExpirationMinutes < 1) {
            setFormError(translate("sales-payment-settings-required-error"));
            return;
        }
        const gatewayUrls = [form.zarinpalCallbackUrl, form.paymentResultUrl];
        if (gatewayUrls.some((value) => value && !isHttpUrl(value))
            || (form.zarinpalEnabled && gatewayUrls.some((value) => !value))) {
            setFormError(translate("sales-payment-settings-url-error"));
            return;
        }

        const payload = {
            defaultCurrency: form.defaultCurrency.trim().toUpperCase(),
            invoicePrefix: form.invoicePrefix.trim().toUpperCase(),
            defaultTaxRate: Number(form.defaultTaxRate || 0),
            paymentExpirationMinutes: Number(form.paymentExpirationMinutes || 30),
            zarinpalEnabled: Boolean(form.zarinpalEnabled),
            zarinpalSandbox: Boolean(form.zarinpalSandbox),
            zarinpalCallbackUrl: form.zarinpalCallbackUrl.trim() || null,
            paymentResultUrl: form.paymentResultUrl.trim() || null,
            manualPaymentInstructions: form.manualPaymentInstructions.trim() || null,
            supportChatUrl: form.supportChatUrl.trim() || null,
            supportMessengerUrl: form.supportMessengerUrl.trim() || null,
            salesPhone: form.salesPhone.trim() || null,
            bankAccounts: form.bankAccounts.map((account) => ({
                ...(account.id ? {id: account.id} : {}),
                bankName: String(account.bankName || "").trim() || null,
                accountHolder: String(account.accountHolder || "").trim() || null,
                cardNumber: String(account.cardNumber || "").trim() || null,
                iban: String(account.iban || "").trim() || null,
                accountNumber: String(account.accountNumber || "").trim() || null,
                active: Boolean(account.active),
            })),
        };

        setSaving(true);
        try {
            const existedBeforeSave = Boolean(billingSetting);
            await saveBillingSettings(payload);
            await getBillingSettings();
            setVisible(false);
            setSuccess(translate(existedBeforeSave
                ? "sales-payment-settings-updated-success"
                : "sales-payment-settings-created-success"));
        } catch (requestError) {
            setFormError(localizedError(requestError));
        } finally {
            setSaving(false);
        }
    };

    const requestError = error ? localizedError(error) : null;
    const gatewayEnabled = billingSetting?.zarinpalEnabled === true;
    const sandboxEnabled = billingSetting?.zarinpalSandbox !== false;

    return (
        <div className="w-full flex flex-column gap-3 text-xs rtl">
            <SalesSectionHeader
                title={translate("sales-payment-settings-title")}
                description={translate("sales-payment-settings-description")}
                actions={(
                    <Button
                        className="text-xs gap-2"
                        icon={billingSetting ? "pi pi-pencil" : "pi pi-plus"}
                        label={translate(billingSetting ? "sales-edit-payment-settings" : "sales-create-payment-settings")}
                        size="small"
                        loading={opening}
                        disabled={loading || saving}
                        onClick={openDialog}
                    />
                )}
            />

            {success && (
                <Message className="w-full text-xs" severity="success" text={success} pt={{text: {className: "text-xs"}}}/>
            )}

            <DataState loading={loading} error={requestError} empty={false}>
                {!billingSetting ? (
                    <Message
                        className="w-full text-xs"
                        severity="info"
                        text={translate("sales-payment-settings-empty")}
                        pt={{text: {className: "text-xs"}}}
                    />
                ) : (
                    <>
                        <div className="grid">
                            <StatCard
                                label={translate("sales-payment-gateway-status")}
                                value={translate(gatewayEnabled ? "sales-status-active" : "sales-status-inactive")}
                                icon="pi pi-credit-card"
                                tone={gatewayEnabled ? "green" : "red"}
                            />
                            <StatCard
                                label={translate("sales-payment-gateway-mode")}
                                value={translate(sandboxEnabled ? "sales-payment-sandbox" : "sales-payment-production")}
                                icon="pi pi-server"
                                tone={sandboxEnabled ? "orange" : "green"}
                            />
                            <StatCard label={translate("sales-default-currency")} value={billingSetting.defaultCurrency || "-"} icon="pi pi-money-bill" tone="blue"/>
                            <StatCard label={translate("sales-payment-expiration")} value={`${billingSetting.paymentExpirationMinutes || 0} ${translate("sales-minutes")}`} icon="pi pi-clock"/>
                        </div>

                        <div className="grid">
                            <div className="col-12 lg:col-6">
                                <Card className="h-full border-1 border-200 shadow-none" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                                    <Divider align="left"><span className="text-xs font-bold">{translate("sales-payment-gateway-section")}</span></Divider>
                                    <InfoRow
                                        label={translate("sales-payment-gateway-status")}
                                        value={(
                                            <Tag className="text-xs" severity={gatewayEnabled ? "success" : "danger"} value={translate(gatewayEnabled ? "sales-status-active" : "sales-status-inactive")}/>
                                        )}
                                    />
                                    <InfoRow label={translate("sales-zarinpal-callback-url")} value={billingSetting.zarinpalCallbackUrl} ltr/>
                                    <InfoRow label={translate("sales-payment-result-url")} value={billingSetting.paymentResultUrl} ltr/>
                                    <InfoRow label={translate("sales-invoice-prefix")} value={billingSetting.invoicePrefix}/>
                                    <InfoRow label={translate("sales-default-tax-rate")} value={`${billingSetting.defaultTaxRate || 0}%`}/>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-6">
                                <Card className="h-full border-1 border-200 shadow-none" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                                    <Divider align="left"><span className="text-xs font-bold">{translate("sales-payment-support-section")}</span></Divider>
                                    <InfoRow label={translate("sales-sales-phone")} value={billingSetting.salesPhone} ltr/>
                                    <InfoRow label={translate("sales-support-chat-url")} value={billingSetting.supportChatUrl} ltr/>
                                    <InfoRow label={translate("sales-support-messenger-url")} value={billingSetting.supportMessengerUrl} ltr/>
                                    <InfoRow label={translate("sales-manual-payment-instructions")} value={billingSetting.manualPaymentInstructions}/>
                                </Card>
                            </div>
                        </div>

                        <Card className="border-1 border-200 shadow-none" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                            <Divider align="left"><span className="text-xs font-bold">{translate("sales-bank-accounts")}</span></Divider>
                            {!billingSetting.bankAccounts?.length ? (
                                <Message className="w-full text-xs" severity="info" text={translate("sales-bank-accounts-empty")} pt={{text: {className: "text-xs"}}}/>
                            ) : (
                                <div className="grid">
                                    {billingSetting.bankAccounts.map((account, index) => (
                                        <div className="col-12 md:col-6 xl:col-4" key={account.id || index}>
                                            <Card className="h-full border-1 border-200 shadow-none" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                                                <div className="flex align-items-center justify-content-between gap-2 mb-2">
                                                    <span className="text-xs font-bold">{account.bankName || translate("sales-bank-account")}</span>
                                                    <Tag className="text-xs" severity={account.active !== false ? "success" : "secondary"} value={translate(account.active !== false ? "sales-status-active" : "sales-status-inactive")}/>
                                                </div>
                                                <InfoRow label={translate("sales-account-holder")} value={account.accountHolder}/>
                                                <InfoRow label={translate("sales-card-number")} value={account.cardNumber} ltr/>
                                                <InfoRow label={translate("sales-iban")} value={account.iban} ltr/>
                                                <InfoRow label={translate("sales-account-number")} value={account.accountNumber} ltr/>
                                            </Card>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </>
                )}
            </DataState>

            <FormDialog
                visible={visible}
                title={translate(billingSetting ? "sales-edit-payment-settings-title" : "sales-create-payment-settings-title")}
                description={translate("sales-payment-settings-form-description")}
                saving={saving}
                error={formError}
                onHide={() => !saving && setVisible(false)}
                onSubmit={save}
                submitLabelKey={billingSetting ? "save" : "create"}
            >
                <div className="col-12">
                    <Message className="w-full text-xs" severity="info" text={translate("sales-payment-merchant-env-hint")} pt={{text: {className: "text-xs"}}}/>
                </div>

                <div className="col-12">
                    <Divider align="left"><span className="text-xs font-bold">{translate("sales-payment-general-section")}</span></Divider>
                </div>
                <FormField label={translate("sales-default-currency")} required>
                    <InputText className="w-full text-xs ltr text-left" value={form.defaultCurrency} onChange={(event) => setForm((value) => ({...value, defaultCurrency: event.target.value}))}/>
                </FormField>
                <FormField label={translate("sales-invoice-prefix")} required>
                    <InputText className="w-full text-xs ltr text-left" value={form.invoicePrefix} onChange={(event) => setForm((value) => ({...value, invoicePrefix: event.target.value}))}/>
                </FormField>
                <FormField label={translate("sales-default-tax-rate")} required>
                    <InputNumber className="w-full text-xs" inputClassName="w-full text-xs" value={form.defaultTaxRate} min={0} max={100} minFractionDigits={0} maxFractionDigits={2} suffix="%" onValueChange={(event) => setForm((value) => ({...value, defaultTaxRate: event.value ?? 0}))}/>
                </FormField>
                <FormField label={translate("sales-payment-expiration")} required hint={translate("sales-payment-expiration-hint")}>
                    <InputNumber className="w-full text-xs" inputClassName="w-full text-xs" value={form.paymentExpirationMinutes} min={1} suffix={` ${translate("sales-minutes")}`} onValueChange={(event) => setForm((value) => ({...value, paymentExpirationMinutes: event.value ?? 30}))}/>
                </FormField>

                <div className="col-12">
                    <Divider align="left"><span className="text-xs font-bold">{translate("sales-payment-gateway-section")}</span></Divider>
                </div>
                <SwitchField label={translate("sales-zarinpal-enabled")} checked={form.zarinpalEnabled} onChange={(zarinpalEnabled) => setForm((value) => ({...value, zarinpalEnabled}))}/>
                <SwitchField label={translate("sales-zarinpal-sandbox")} checked={form.zarinpalSandbox} onChange={(zarinpalSandbox) => setForm((value) => ({...value, zarinpalSandbox}))}/>
                <FormField label={translate("sales-zarinpal-callback-url")} required={form.zarinpalEnabled} className="col-12" hint={translate("sales-zarinpal-callback-url-hint")}>
                    <InputText className="w-full text-xs ltr text-left" value={form.zarinpalCallbackUrl} placeholder={translate("sales-zarinpal-callback-url-placeholder")} onChange={(event) => setForm((value) => ({...value, zarinpalCallbackUrl: event.target.value}))}/>
                </FormField>
                <FormField label={translate("sales-payment-result-url")} required={form.zarinpalEnabled} className="col-12" hint={translate("sales-payment-result-url-hint")}>
                    <InputText className="w-full text-xs ltr text-left" value={form.paymentResultUrl} placeholder={translate("sales-payment-result-url-placeholder")} onChange={(event) => setForm((value) => ({...value, paymentResultUrl: event.target.value}))}/>
                </FormField>

                <div className="col-12">
                    <Divider align="left"><span className="text-xs font-bold">{translate("sales-payment-support-section")}</span></Divider>
                </div>
                <FormField label={translate("sales-sales-phone")}>
                    <InputText className="w-full text-xs ltr text-left" value={form.salesPhone} onChange={(event) => setForm((value) => ({...value, salesPhone: event.target.value}))}/>
                </FormField>
                <FormField label={translate("sales-support-chat-url")}>
                    <InputText className="w-full text-xs ltr text-left" value={form.supportChatUrl} onChange={(event) => setForm((value) => ({...value, supportChatUrl: event.target.value}))}/>
                </FormField>
                <FormField label={translate("sales-support-messenger-url")} className="col-12">
                    <InputText className="w-full text-xs ltr text-left" value={form.supportMessengerUrl} onChange={(event) => setForm((value) => ({...value, supportMessengerUrl: event.target.value}))}/>
                </FormField>
                <FormField label={translate("sales-manual-payment-instructions")} className="col-12">
                    <InputTextarea className="w-full text-xs text-right" rows={4} autoResize value={form.manualPaymentInstructions} onChange={(event) => setForm((value) => ({...value, manualPaymentInstructions: event.target.value}))}/>
                </FormField>

                <div className="col-12">
                    <Divider align="left"><span className="text-xs font-bold">{translate("sales-bank-accounts")}</span></Divider>
                    <div className="flex flex-column gap-3">
                        {form.bankAccounts.map((account, index) => (
                            <Card key={account.id || index} className="border-1 border-200 shadow-none" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                                <div className="flex align-items-center justify-content-between gap-2 mb-3">
                                    <span className="text-xs font-bold">{translate("sales-bank-account")} {index + 1}</span>
                                    <Button type="button" className="text-xs gap-2" icon="pi pi-trash" label={translate("sales-remove-bank-account")} severity="danger" outlined onClick={() => removeBankAccount(index)}/>
                                </div>
                                <div className="grid m-0">
                                    <FormField label={translate("sales-bank-name")}>
                                        <InputText className="w-full text-xs" value={account.bankName} onChange={(event) => updateBankAccount(index, "bankName", event.target.value)}/>
                                    </FormField>
                                    <FormField label={translate("sales-account-holder")}>
                                        <InputText className="w-full text-xs" value={account.accountHolder} onChange={(event) => updateBankAccount(index, "accountHolder", event.target.value)}/>
                                    </FormField>
                                    <FormField label={translate("sales-card-number")}>
                                        <InputText className="w-full text-xs ltr text-left" value={account.cardNumber} onChange={(event) => updateBankAccount(index, "cardNumber", event.target.value)}/>
                                    </FormField>
                                    <FormField label={translate("sales-iban")}>
                                        <InputText className="w-full text-xs ltr text-left" value={account.iban} onChange={(event) => updateBankAccount(index, "iban", event.target.value)}/>
                                    </FormField>
                                    <FormField label={translate("sales-account-number")}>
                                        <InputText className="w-full text-xs ltr text-left" value={account.accountNumber} onChange={(event) => updateBankAccount(index, "accountNumber", event.target.value)}/>
                                    </FormField>
                                    <SwitchField label={translate("sales-bank-account-active")} checked={account.active} onChange={(active) => updateBankAccount(index, "active", active)}/>
                                </div>
                            </Card>
                        ))}
                        <Button type="button" className="text-xs gap-2 align-self-start" icon="pi pi-plus" label={translate("sales-add-bank-account")} severity="secondary" outlined onClick={addBankAccount}/>
                    </div>
                </div>
            </FormDialog>
        </div>
    );
}

export default PaymentSettingsTab;
